/**
 * The overlay primitive. One component, two presentations, seventeen callers.
 *
 * Below `md` it is a bottom sheet: rises from the bottom edge, rounded top corners, a drag
 * handle, capped at 85% of the viewport with its content scrolling inside — reachable by thumb,
 * which on a phone is the whole point. From `md` it is a centred dialog, at most 480px wide,
 * rounded on every corner and never touching an edge — a sheet pinned to the bottom of a
 * 1440px monitor was the wrong shape there, cut off and with its buttons against the screen edge.
 *
 * The switch is CSS only — the same element, the same DOM, the same focus trap. Focus trapping,
 * scroll lock, Escape and focus restoration come from `useDialogHost` in `@pos/shared`, and none
 * of that is touched here: getting it subtly wrong is easy and both apps need the same answer.
 */

import { useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useDialogHost } from '@pos/shared';

import { cn } from './cn.js';

export type SheetProps = {
  open: boolean;
  onDismiss: () => void;
  title?: string;
  /** Off for destructive questions: a stray tap must not answer one. */
  dismissOnBackdrop?: boolean;
  /** False for a covered sheet, so two traps do not fight over focus. */
  active?: boolean;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
};

export function Sheet({
  open,
  onDismiss,
  title,
  dismissOnBackdrop = true,
  active = true,
  children,
  footer,
  className,
}: SheetProps) {
  const dismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  const containerRef = useDialogHost(open && active, dismiss);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-dialog flex items-end justify-center md:items-center md:p-6"
      data-print="hide"
    >
      <div
        className="absolute inset-0 bg-scrim animate-[fade_140ms_ease-out]"
        onClick={dismissOnBackdrop ? dismiss : undefined}
        aria-hidden="true"
      />

      <section
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'relative flex w-full flex-col bg-surface-overlay outline-none',
          // Phone: a sheet. Full width, top corners, 85% cap, safe-area at the foot.
          'max-h-[85dvh] max-w-column rounded-t-sheet pb-[env(safe-area-inset-bottom)]',
          'motion-safe:animate-[rise_190ms_var(--pos-ease-spring)]',
          // From md: a dialog. Capped, every corner, no safe-area, lifted by the overlay shadow.
          'md:max-h-[85dvh] md:max-w-[480px] md:rounded-sheet md:pb-0 md:shadow-[var(--pos-elevation-overlay)]',
          'md:motion-safe:animate-[settle_190ms_var(--pos-ease-spring)]',
          className,
        )}
      >
        {/* A grab handle, because a sheet without one does not read as draggable-dismissible
            even when it is dismissible by tap. A dialog has nothing to drag; it goes. */}
        <div className="flex justify-center pt-3 md:hidden" aria-hidden="true">
          <span className="h-1 w-10 rounded-full bg-line" />
        </div>

        {title !== undefined && (
          <h2 className="px-6 pb-1 pt-4 text-xl font-semibold text-content-primary md:pt-6">{title}</h2>
        )}

        <div className="scroll-region flex-1 px-6 py-4">{children}</div>

        {footer !== undefined && (
          <div className="flex gap-3 border-t border-line-subtle px-6 py-4">{footer}</div>
        )}
      </section>

      <style>{`
        @keyframes rise { from { transform: translateY(14%); opacity: .6 } to { transform: none; opacity: 1 } }
        @keyframes settle { from { transform: translateY(8px) scale(.98); opacity: 0 } to { transform: none; opacity: 1 } }
        @keyframes fade { from { opacity: 0 } to { opacity: 1 } }
      `}</style>
    </div>,
    document.body,
  );
}
